import React, { useEffect, useRef, useState } from 'react';

const PinchAnimation = () => (
  <div style={{ position: 'relative', width: 180, height: 180 }}>
    <style>{`
      @keyframes pinchZoom {
        0%   { transform: translate(-50%, -50%) scale(1);   opacity: 1; }
        40%  { transform: translate(-50%, -50%) scale(0.55); opacity: 1; }
        60%  { transform: translate(-50%, -50%) scale(0.55); opacity: 1; }
        100% { transform: translate(-50%, -50%) scale(1);   opacity: 1; }
      }
      @keyframes thumbMove {
        0%   { transform: translate(0px, 0px) rotate(-30deg); }
        40%  { transform: translate(22px, 22px) rotate(-15deg); }
        60%  { transform: translate(22px, 22px) rotate(-15deg); }
        100% { transform: translate(0px, 0px) rotate(-30deg); }
      }
      @keyframes indexMove {
        0%   { transform: translate(0px, 0px) rotate(30deg); }
        40%  { transform: translate(-22px, -22px) rotate(15deg); }
        60%  { transform: translate(-22px, -22px) rotate(15deg); }
        100% { transform: translate(0px, 0px) rotate(30deg); }
      }
      @keyframes ripple {
        0%   { transform: translate(-50%, -50%) scale(0.3); opacity: 0.7; }
        100% { transform: translate(-50%, -50%) scale(1.6); opacity: 0; }
      }
      .pinch-container {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        width: 120px;
        height: 120px;
        animation: pinchZoom 2.2s ease-in-out infinite;
      }
      .finger-thumb {
        position: absolute;
        bottom: 18px;
        left: 18px;
        animation: thumbMove 2.2s ease-in-out infinite;
        transform-origin: bottom center;
      }
      .finger-index {
        position: absolute;
        top: 18px;
        right: 18px;
        animation: indexMove 2.2s ease-in-out infinite;
        transform-origin: top center;
      }
      .ripple-ring {
        position: absolute;
        top: 50%;
        left: 50%;
        width: 70px;
        height: 70px;
        border-radius: 50%;
        border: 2px solid rgba(255,255,255,0.5);
        animation: ripple 2.2s ease-out infinite;
      }
      .ripple-ring-2 {
        animation-delay: 1.1s;
      }
    `}</style>

    {/* Ripple rings at center */}
    <div className="ripple-ring" />
    <div className="ripple-ring ripple-ring-2" />

    {/* Animated fingers */}
    <div className="pinch-container">
      {/* Thumb finger */}
      <div className="finger-thumb">
        <svg width="38" height="58" viewBox="0 0 38 58" fill="none">
          <rect x="8" y="0" width="22" height="42" rx="11" fill="rgba(255,220,180,0.95)" stroke="rgba(200,160,120,0.8)" strokeWidth="1.5"/>
          <rect x="8" y="36" width="22" height="18" rx="4" fill="rgba(240,200,160,0.95)" stroke="rgba(200,160,120,0.8)" strokeWidth="1.5"/>
          <line x1="19" y1="10" x2="19" y2="32" stroke="rgba(200,160,120,0.4)" strokeWidth="1" strokeLinecap="round"/>
        </svg>
      </div>

      {/* Index finger */}
      <div className="finger-index">
        <svg width="38" height="58" viewBox="0 0 38 58" fill="none">
          <rect x="8" y="16" width="22" height="42" rx="11" fill="rgba(255,220,180,0.95)" stroke="rgba(200,160,120,0.8)" strokeWidth="1.5"/>
          <rect x="8" y="16" width="22" height="18" rx="4" fill="rgba(240,200,160,0.95)" stroke="rgba(200,160,120,0.8)" strokeWidth="1.5"/>
          <line x1="19" y1="26" x2="19" y2="48" stroke="rgba(200,160,120,0.4)" strokeWidth="1" strokeLinecap="round"/>
        </svg>
      </div>
    </div>
  </div>
);

const ARTutorialOverlay = ({ onDismiss }) => {
  const [visible, setVisible] = useState(true);
  const [fadeOut, setFadeOut] = useState(false);

  // Store onDismiss in a ref so it never appears in the useEffect dependency array.
  // This prevents iOS Safari re-renders from restarting the timers.
  const onDismissRef = useRef(onDismiss);
  useEffect(() => { onDismissRef.current = onDismiss; }, [onDismiss]);

  useEffect(() => {
    // Start fade-out at 2.5s, fully hide at 3s
    const fadeTimer = setTimeout(() => setFadeOut(true), 2500);
    const hideTimer = setTimeout(() => {
      setVisible(false);
      if (onDismissRef?.current) onDismissRef?.current();
    }, 3000);

    return () => {
      clearTimeout(fadeTimer);
      clearTimeout(hideTimer);
    };
    // Empty dependency array: timers are set once on mount and never restarted
  }, []);

  if (!visible) return null;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'rgba(0,0,0,0.45)',
        transition: 'opacity 0.5s ease',
        opacity: fadeOut ? 0 : 1,
        pointerEvents: fadeOut ? 'none' : 'auto',
      }}
    >
      {/* Instruction text */}
      <p
        style={{
          color: '#fff',
          fontSize: '1rem',
          fontWeight: 600,
          letterSpacing: '0.02em',
          marginBottom: '1rem',
          textAlign: 'center',
          textShadow: '0 2px 8px rgba(0,0,0,0.6)',
          padding: '0 1.5rem',
        }}
      >
        Use two fingers to zoom &amp; move the dish
      </p>

      {/* Animated pinch-to-zoom gesture */}
      <PinchAnimation />

      {/* Sub-label */}
      <p
        style={{
          color: 'rgba(255,255,255,0.75)',
          fontSize: '0.8rem',
          marginTop: '1rem',
          textAlign: 'center',
          textShadow: '0 1px 4px rgba(0,0,0,0.5)',
        }}
      >
        Pinch to zoom · Drag to move
      </p>
    </div>
  );
};

export default ARTutorialOverlay;
